import { useState } from "react";
import { StyleSheet } from "react-native";
import { View } from "react-native-web";
import { DocumentLevelComponent } from "platform-specific/popup";
import { UtilityText } from 'component/text';
import { colorSurfacePrimary, colorTextHint, colorTextOnDarkBackground, colorTextOnLightColorTag, colorTextPrimary } from 'component/color';
import { ShadowBox } from 'component/basics';
import { Information } from "@carbon/icons-react";

const tooltipStyle = StyleSheet.create({
    toolTip: {
        position: "absolute",
        flexDirection: "column",
        borderRadius: 8,
        padding: 12,
        backgroundColor: colorSurfacePrimary,
        boxShadow: "0px 10px 24px rgba(0, 0, 0, 0.1)" as any, // FIXME: boxShadow not typed for native
        zIndex: 1
    },
    tooltipContainer: {
        width: "100%",
    }
})

export function ToolTip({ toolTipWidget, label, text, children, disabled = false, offset = 0, width = 0, testID }: Readonly<{ toolTipWidget?: JSX.Element; label?: string; text?: string; children?: any; disabled?: boolean; offset?: number; width?: number; testID?: string }>) {
    const [isHovering, setIsHovering] = useState(false);
    return (
        <View
            style={[tooltipStyle.tooltipContainer, { width: width || null }]}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            testID={testID}
        >
            {isHovering && !disabled && (
                <View style={{ position: "relative" }}>
                    <View style={[tooltipStyle.toolTip, { bottom: offset }]}>
                        {toolTipWidget || <UtilityText label={label} text={text} />}
                    </View>
                </View>
            )}
            {children}
        </View>
    );
}

const DocumentLevelTooltipStyle = StyleSheet.create({
    tooltipContainer: {
        position: "absolute",
        zIndex: 2000,
    },
    tooltipBase: {
        position: "absolute",
    },
    defaultTooltip: {
        flexDirection: "column",
        borderRadius: 8,
        padding: 12,
        backgroundColor: colorSurfacePrimary,
        zIndex: 1,
    },
});

export function DocumentLevelTooltip({ tooltipWidget, label, text, children, disabled = false, offset = 0, width = 0, color = colorSurfacePrimary, textColor = colorTextPrimary, testID }: Readonly<{ tooltipWidget?: JSX.Element; label?: string; text?: string; children?: any; disabled?: boolean; offset?: number; width?: number; color?: string; textColor?: string; testID?: string }>) {
    const s = DocumentLevelTooltipStyle;

    const [isHovering, setIsHovering] = useState(false);
    const [pos, setPos] = useState({ x: null, y: null });

    return (
        <View
            onMouseEnter={(e) => {
                setPos({ x: e.clientX, y: e.clientY });
                setIsHovering(true);
            }}
            onMouseLeave={() => setIsHovering(false)}
            testID={testID}
        >
            {isHovering && !disabled && (
                <DocumentLevelComponent>
                    <View
                        style={[
                            s.tooltipContainer,
                            {
                                left: pos.x,
                                top: pos.y,
                                width: width || null,
                            },
                        ]}
                    >
                        <View style={[s.tooltipBase, { bottom: offset }]}>
                            {tooltipWidget || (
                                <ShadowBox>
                                    <View style={[s.defaultTooltip, { backgroundColor: color }]}>
                                        <UtilityText label={label} text={text} color={textColor} />
                                    </View>
                                </ShadowBox>
                            )}
                        </View>
                    </View>
                </DocumentLevelComponent>
            )}
            {children}
        </View>
    );
}

export function InfoTag({ label, text, labelWidget, testID }: { label?: string; text?: string; labelWidget?: JSX.Element, testID?: string }) {
    return (
        <View testID={ testID ?? label ?? text}>
            <DocumentLevelTooltip
                label={label}
                text={text}
                tooltipWidget={labelWidget}
                width={215}
                color={colorTextOnLightColorTag}
                textColor={colorTextOnDarkBackground}
            >
                <Information size={14} color={colorTextHint} />
            </DocumentLevelTooltip>
        </View>
    );
}